/**
 * A seat in a room, client side.
 *
 * The shape of this file is the shape of the change it came out of. It used to
 * be a lockstep client: it gated the frame loop on a round bundle, held local
 * orders back two rounds so every machine could apply them on the same tick, and
 * compared world hashes with its peers to find out whether the identical
 * simulations had stopped being identical.
 *
 * None of that is here, because the realm is no longer this machine's to run.
 * The server simulates it and sends the board; this file receives it. What is
 * left is small on purpose:
 *
 *   - `send(cmds, world)` — orders go out the moment the player gives them.
 *     There is no round to wait for and no ally to wait for. What comes back is
 *     the board with the order already in it.
 *   - `onRealm` — the board moved. Ten times a second, and only what changed.
 *   - `field(w)` — the one surface that is still a relay: a battlefield where
 *     two crowns meet is simulated on both machines, so orders there are passed
 *     along rather than applied by the server.
 *
 * Two consequences worth naming, because they are the difference a player will
 * actually feel. An order now costs one trip to the server instead of two fixed
 * rounds, so the map answers in about a third of the time. And an ally on a bad
 * connection no longer stops the room: they fall behind the board, catch up, and
 * nobody else's clock so much as stutters.
 */
import type { Command } from '../sim/commands.ts';
import type { SaveGame } from '../sim/save.ts';
import type { RealmState, RealmUpdate } from './realm.ts';

/** Names come from strangers; they are printed, so they are escaped. */
function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}

/**
 * This browser's own name for itself, kept between visits.
 *
 * Not a login — it proves nothing to anybody — but it is enough to answer the
 * one question a room kept getting wrong: *is this somebody new, or somebody who
 * was already here?* Without it a player who left and came back took a second
 * seat beside their own, and a two-seat room could be filled by one person twice
 * over.
 */
export function playerToken(): string {
  const KEY = 'hearth.coop.pid';
  try {
    const held = localStorage.getItem(KEY);
    if (held) return held;
    const made = `p${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    localStorage.setItem(KEY, made);
    return made;
  } catch {
    // Private browsing: a token for this session only, which still stops a
    // player taking two seats within it.
    return `p${Math.random().toString(36).slice(2)}`;
  }
}

/**
 * Where the server is, worked out rather than asked for.
 *
 * It hosts the game and the room traffic on one port, so the page's own origin
 * *is* the server: `wss:` when the page is TLS, `ws:` when it is not, same host,
 * same port. Nobody types an address, nothing breaks when the VM moves, and a
 * TLS page never tries to open a plaintext socket.
 *
 * `?server=` overrides it, which is how the dev machine — vite on 5173, the
 * server on 8931 — reaches its own, and how anyone can point a client at a
 * different host for a one-off.
 */
export function relayUrl(): string {
  const override = new URLSearchParams(location.search).get('server');
  if (override) return override;
  const scheme = location.protocol === 'https:' ? 'wss:' : 'ws:';
  // Its own path, so the dev server can tell this socket from its own: a proxy
  // rule on `/` swallowed vite's HMR upgrade and forwarded it here, and this
  // answered in a language HMR does not speak.
  return `${scheme}//${location.host}/coop`;
}

/**
 * How long the board may go unmentioned before the panel says so.
 *
 * The server speaks ten times a second, so two seconds is twenty missed
 * updates: comfortably past jitter, well short of accusing a slow moment of
 * being a dead line.
 */
const QUIET_AFTER = 2;

export interface NetPlayer {
  id: number;
  name: string;
  alive: boolean;
}

export interface NetConfig {
  seed: number;
  /** The host's own crown — kept for the realm's seed and its capital. */
  factionId: string;
  raceId: string;
  kingdomId: string;
  difficultyId: string;
  heroId?: string;
}

/**
 * Who holds which crown.
 *
 * A room is not one kingdom shared out; it is several kingdoms on one realm, and
 * the war between them is the point. Each seat brings its own, and the server
 * collects them so every client can name the same crowns in the same order.
 */
export interface Seating {
  id: number;
  name: string;
  factionId: string;
  raceId: string;
  kingdomId: string;
  heroId?: string;
  /** Which crown on the board this is, decided by the server and nobody else. */
  nation: number;
}

/**
 * A crown this room is keeping for somebody who is not in it.
 *
 * The lobby's one genuinely new idea. A seat that is merely empty is an
 * invitation; a seat that is *kept* belongs to the player who built that
 * kingdom last night and has not signed in yet, and the difference has to be on
 * screen or the first person through the door takes it in good faith.
 */
export interface AbsentCrown {
  name: string;
  factionId: string;
  raceId: string;
  kingdomId: string;
  heroId?: string;
  nation: number;
}

/** The room itself, as the lobby screen needs it. Arrives with every roster. */
export interface RoomView {
  code: string;
  name: string;
  size: number;
  started: boolean;
  /** The seat that may pause, save and start the room. -1 if the room is empty. */
  host: number;
  /** Whether anybody has said what realm this is. */
  ready: boolean;
  crowned: number;
  absent: AbsentCrown[];
  paused: boolean;
  pausedBy: string;
}

/** A room on disk that could be reopened, as the host's picker needs it. */
export interface SavedRoom {
  code: string;
  name: string;
  size: number;
  /** When the file was last written, in wall-clock milliseconds. */
  when: number;
  /** Whether the room is standing right now rather than only filed. */
  open: boolean;
  started: boolean;
  seed: number;
  /** Whether there is a realm in it, as against only a recipe. */
  realm: boolean;
  crowns: AbsentCrown[];
}

/** A room as the directory sees it, before anybody has joined it. */
export interface RoomInfo {
  code: string;
  name: string;
  host: string;
  seats: number;
  size: number;
  started: boolean;
  cfg: NetConfig | null;
}

/** Everything a seat is handed when it joins: the board, and its own city. */
export interface Begin {
  /** The crown this seat plays, as the server has it. */
  nation: number;
  capital: number;
  seating: Seating[];
  cfg: NetConfig;
  realm: RealmState;
  /** This crown's capital as it was last saved here, or nothing to build one. */
  city: SaveGame | null;
  paused: boolean;
}

/**
 * Ask a server what rooms are open, without joining one.
 *
 * Its own socket, opened and dropped: the lobby browser runs before a session
 * exists and must not leave one behind if the player wanders off.
 */
export function listRooms(url: string, timeoutMs = 4000): Promise<RoomInfo[]> {
  return new Promise((resolve, reject) => {
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch {
      reject(new Error('bad server address'));
      return;
    }
    const timer = setTimeout(() => {
      try {
        ws.close();
      } catch {
        /* already gone */
      }
      reject(new Error('the server did not answer'));
    }, timeoutMs);
    ws.onerror = () => {
      clearTimeout(timer);
      reject(new Error('could not reach the server'));
    };
    ws.onopen = () => ws.send(JSON.stringify({ t: 'list' }));
    ws.onmessage = (ev) => {
      const m = JSON.parse(String(ev.data));
      if (m.t !== 'rooms') return;
      clearTimeout(timer);
      ws.close();
      resolve(m.rooms as RoomInfo[]);
    };
  });
}

/**
 * Ask a server which rooms it could reopen.
 *
 * The same shape as `listRooms` and for the same reason — this runs on the
 * title screen, before a session exists, and must not leave a socket behind if
 * the player wanders off. Two calls rather than one message with both answers
 * in it: a room that is *open* and a room that is *filed* are different offers
 * and the screen makes them separately.
 */
export function listSaves(url: string, timeoutMs = 4000): Promise<SavedRoom[]> {
  return new Promise((resolve, reject) => {
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch {
      reject(new Error('bad server address'));
      return;
    }
    const timer = setTimeout(() => {
      try {
        ws.close();
      } catch {
        /* already gone */
      }
      reject(new Error('the server did not answer'));
    }, timeoutMs);
    ws.onerror = () => {
      clearTimeout(timer);
      reject(new Error('could not reach the server'));
    };
    ws.onopen = () => ws.send(JSON.stringify({ t: 'saves' }));
    ws.onmessage = (ev) => {
      const m = JSON.parse(String(ev.data));
      if (m.t !== 'saves') return;
      clearTimeout(timer);
      ws.close();
      resolve(m.saves as SavedRoom[]);
    };
  });
}

export class Session {
  private ws!: WebSocket;
  playerId = -1;
  isHost = false;
  players: NetPlayer[] = [];
  /** Every seat's chosen crown, in seat order. */
  seating: Seating[] = [];
  /** The crown this client plays. -1 until the server says. */
  nation = -1;
  /**
   * The room, as the server has it: seats, host, and the crowns it is keeping.
   *
   * Null until the first roster arrives, which is immediately after `hello` —
   * the lobby screen paints off this and nothing else, so that what a player is
   * looking at while they wait is the server's own account of the room rather
   * than this client's guess at it.
   */
  view: RoomView | null = null;
  /**
   * The crown the room handed back before it was asked for one.
   *
   * Set when a returning player is seated in the kingdom they played here
   * before. It exists so the client knows *not* to claim something else over the
   * top of it: the kingdom screen was visited on the way in, and sending its
   * answer would move the player off their own nation and away from their city.
   */
  restored: Seating | null = null;
  /** The lobby changed. Fires on rosters, pauses, refusals and notices. */
  onLobby: (() => void) | null = null;
  /** Something the room should know, in the server's words. */
  onNotice: ((why: string) => void) | null = null;
  /** The last refusal this seat was given, for the lobby to print. */
  refusal = '';
  /**
   * The room said no, and the game is already running.
   *
   * Before host authority existed a refusal could only ever arrive instead of a
   * board, so it was enough to hand it to whoever was waiting for one. "Only the
   * host may stop the realm" arrives an hour in, to a client that is playing
   * perfectly happily, and had nowhere to go — it would have been swallowed and
   * the player would have pressed the key again.
   */
  onRefusal: ((why: string) => void) | null = null;
  room = '';
  /** What this client is called in the room. */
  name = '';

  /**
   * A room-wide pause.
   *
   * It is the server's flag now rather than an announcement passed between
   * peers: the realm stops because the process running it stops stepping, which
   * is the only way a pause can mean the same thing to four people at once.
   */
  paused = false;
  pausedBy = '';
  onPause: ((paused: boolean, who: string) => void) | null = null;

  /**
   * The board moved. Every hundred milliseconds, carrying only what changed.
   *
   * Set through `watchRealm`, never directly, because of what an update *is*: a
   * patch against the board the last one left behind. Miss one and every board
   * after it is wrong in a way nothing announces — so an update that arrives
   * before there is a world to put it in is kept rather than dropped. That
   * window is real: it is the second or so a client spends generating a map and
   * loading its own capital.
   */
  private onRealm: ((u: RealmUpdate) => void) | null = null;
  private backlog: RealmUpdate[] = [];
  /** Orders on a field both crowns are standing on, from the other crown. */
  onFieldOrders: ((cmds: Command[]) => void) | null = null;
  /** Why the room ended, if it has. */
  closed = '';
  onClosed: ((why: string) => void) | null = null;

  private cfg: NetConfig | null = null;
  private cfgWaiters: ((c: NetConfig) => void)[] = [];
  private beginWaiters: { ok: (b: Begin) => void; no: (e: Error) => void }[] = [];
  private begun: Begin | null = null;
  private statusEl: HTMLElement | null = null;
  /** When the last board arrived, so the panel can say the line has gone quiet. */
  private lastHeard = 0;

  connect(
    url: string,
    roomCode: string,
    host: boolean,
    players: number,
    nickname: string,
    roomName?: string,
    /**
     * The sign-in this seat is taken under, on a hearth that asks for one.
     *
     * Sent once, in the first message, and never held by this class: the server
     * checks the signature on it and everything after that is a seat id. A
     * hearth that does not ask ignores it, which is what lets one client talk to
     * both kinds without knowing which it has reached.
     */
    proof?: { token: string | null },
  ): Promise<void> {
    this.room = roomCode;
    this.name = nickname;
    return new Promise((resolve, reject) => {
      this.ws = new WebSocket(url);
      this.ws.onerror = () => reject(new Error(`could not reach the server at ${url}`));
      this.ws.onopen = () => {
        this.send({
          t: 'hello',
          room: roomCode,
          host,
          players,
          name: nickname,
          roomName,
          pid: playerToken(),
          token: proof?.token ?? undefined,
        });
      };
      this.ws.onclose = () => {
        if (this.closed) return;
        this.closed = 'the connection dropped';
        this.onClosed?.(this.closed);
        // Anyone still waiting to be let in never will be.
        for (const w of this.beginWaiters) w.no(new Error(this.closed));
        this.beginWaiters.length = 0;
        this.paint();
      };
      this.ws.onmessage = (ev) => {
        const m = JSON.parse(String(ev.data));
        if (m.t === 'welcome') {
          this.playerId = m.id;
          this.isHost = !!m.host;
          this.players = m.players;
          this.seating = (m.seating ?? this.seating) as Seating[];
          this.restored = (m.seat ?? null) as Seating | null;
          this.takeView(m.room);
          resolve();
        } else if (m.t === 'roster') {
          this.players = m.players;
          this.seating = (m.seating ?? []) as Seating[];
          this.takeView(m.room);
          this.paint();
        } else if (m.t === 'notice') {
          const why = String(m.why ?? '');
          this.onNotice?.(why);
          this.onLobby?.();
        } else if (m.t === 'config') {
          this.cfg = m.cfg;
          for (const w of this.cfgWaiters) w(m.cfg);
          this.cfgWaiters.length = 0;
        } else if (m.t === 'begin') {
          const b = m as unknown as Begin;
          this.begun = b;
          this.nation = b.nation;
          this.seating = b.seating ?? [];
          this.takeView((m as Record<string, unknown>).room);
          this.cfg = b.cfg;
          this.paused = !!b.paused;
          this.lastHeard = Date.now();
          for (const w of this.beginWaiters) w.ok(b);
          this.beginWaiters.length = 0;
          this.paint();
        } else if (m.t === 'realm') {
          this.lastHeard = Date.now();
          if (this.onRealm) this.onRealm(m as RealmUpdate);
          else this.backlog.push(m as RealmUpdate);
        } else if (m.t === 'orders') {
          this.onFieldOrders?.((m.c ?? []) as Command[]);
        } else if (m.t === 'pause') {
          this.paused = !!m.on;
          this.pausedBy = String(m.who ?? '');
          this.onPause?.(this.paused, this.pausedBy);
          this.onLobby?.();
          this.paint();
        } else if (m.t === 'closed') {
          this.closed = String(m.why ?? 'the room closed');
          this.onClosed?.(this.closed);
          this.paint();
        } else if (m.t === 'error') {
          /**
           * A refusal, and it has to reach whoever is waiting.
           *
           * "That crown is already taken", "that room is full" — both arrive
           * *instead of* a board, so a client that only painted the sentence sat
           * on a black screen forever waiting for a room it was never going to
           * be let into. It goes to the panel and to the waiter.
           */
          const why = String(m.why ?? 'the room refused');
          this.refusal = why;
          this.paint(why);
          this.onLobby?.();
          this.onRefusal?.(why);
          /**
           * A refusal is not always fatal, and telling the two apart is what
           * keeps the lobby usable.
           *
           * "That crown is taken" arrives *instead of* a board and has to end
           * the wait, or the client sits in front of a room it will never be let
           * into. "Only the host may pause" arrives while the room is perfectly
           * fine and must not throw anybody out of it — so once this seat has
           * been handed a board, a refusal is a sentence on the screen and
           * nothing more.
           */
          if (!this.begun) {
            for (const w of this.beginWaiters) w.no(new Error(why));
            this.beginWaiters.length = 0;
          }
        }
      };
    });
  }

  /**
   * Start drawing the board, beginning with whatever arrived while it was being
   * built. In order, because an update is a patch and patches are a sequence.
   */
  watchRealm(cb: (u: RealmUpdate) => void): void {
    this.onRealm = cb;
    for (const u of this.backlog) cb(u);
    this.backlog.length = 0;
  }

  /** Tell the room which crown this seat is taking. */
  claimCrown(s: Omit<Seating, 'id' | 'name' | 'nation'>): void {
    this.send({ t: 'crown', ...s });
  }

  /** Whether this seat holds the room: the pause, the save and the horn. */
  get holdsRoom(): boolean {
    return this.view ? this.view.host === this.playerId : this.isHost;
  }

  /** Host: start the room now, with whoever has taken a crown. */
  beginNow(): void {
    this.send({ t: 'begin' });
  }

  /** Host: free a crown whose player has not come back for it. */
  releaseCrown(nation: number): void {
    this.send({ t: 'release', nation });
  }

  /** Host: file the room — the realm and every capital — where it stands. */
  saveRoom(): void {
    this.send({ t: 'roomsave' });
  }

  /** The room's own account of itself, kept whenever the server sends one. */
  private takeView(v: unknown): void {
    if (!v || typeof v !== 'object') return;
    this.view = v as RoomView;
    this.isHost = this.view.host === this.playerId;
    this.onLobby?.();
  }

  /** Host: publish the world recipe. The room is built from this and only this. */
  shareConfig(cfg: NetConfig): void {
    this.cfg = cfg;
    this.send({ t: 'config', cfg });
  }

  waitConfig(): Promise<NetConfig> {
    if (this.cfg) return Promise.resolve(this.cfg);
    return new Promise((res) => this.cfgWaiters.push(res));
  }

  /**
   * Wait for the board.
   *
   * One door into a room rather than the two lockstep needed. There is no
   * difference any more between opening a room and walking into one that has
   * been running for an hour: both are the server handing over the realm as it
   * stands, and the second one is simply a realm with more in it.
   */
  waitBegin(): Promise<Begin> {
    if (this.begun) return Promise.resolve(this.begun);
    return new Promise((ok, no) => this.beginWaiters.push({ ok, no }));
  }

  /**
   * Ask the room to stop, or to start again. **The host's, and only theirs.**
   *
   * Still called "ask" rather than "set", which was already the honest word for
   * it and is now literally true: the server decides, and a seat that is not the
   * host is refused and told so. Nothing here checks first — a client that
   * enforces its own permissions is a client that can be edited out of them, and
   * the rule that matters is the one on the machine holding the realm.
   */
  setPaused(on: boolean): void {
    this.send({ t: 'pause', on });
  }

  /**
   * Orders for the shared realm. They leave now and land when they land.
   *
   * Nothing is applied locally on the way out — not even by this client, to its
   * own board. That is what "authoritative" costs and what it buys: the realm a
   * player is looking at is the realm as it actually is, rather than a
   * prediction that has to be taken back when the server disagrees.
   */
  order(cmds: readonly Command[]): void {
    if (!cmds.length) return;
    this.send({ t: 'cmd', w: 1, c: cmds });
  }

  /** Orders on a field two crowns are standing on. Relayed, not simulated. */
  orderField(cmds: readonly Command[]): void {
    if (!cmds.length) return;
    this.send({ t: 'cmd', w: 2, c: cmds });
  }

  /**
   * Mirror this crown's own capital to the room.
   *
   * The realm is not sent: it is the server's, and the server writes it. A city
   * is simulated on exactly one machine — this one — so this is the only copy
   * there is, and the room keeps it under the crown it belongs to so that
   * reopening the room puts each player back in their own.
   */
  pushCity(blob: SaveGame): void {
    this.send({ t: 'save', city: blob });
  }

  /**
   * Notice silence.
   *
   * Lockstep announced a bad line by stopping the game — you could not cross a
   * round boundary without your allies, so trouble was loud and immediate. This
   * model has the opposite failure: the client is only drawing, so a server that
   * has stopped talking looks exactly like a realm where nothing is happening,
   * which on a strategic map is most of the time. So the panel watches the
   * clock. Called every frame, and repaints only when the answer changes.
   */
  heartbeat(): void {
    // A paused room says nothing because there is nothing to say, and that is
    // not silence: the pause itself arrived over the same wire.
    const quiet =
      this.begun !== null && !this.closed && !this.paused && this.quietFor() > QUIET_AFTER;
    if (quiet === this.quiet) return;
    this.quiet = quiet;
    this.paint();
  }
  private quiet = false;

  /**
   * Leave the room properly rather than by walking out.
   *
   * The socket closing is how the server learns a seat has gone, so a client
   * that dropped its session object without closing it would sit in the roster
   * as a live player who never says anything — and would hold a seat in a room
   * that has a size limit. The realm plays on either way; the room is the
   * server's now.
   */
  leave(): void {
    this.closed = 'you left the room';
    /**
     * Anybody waiting for a board is waiting for one that is not coming.
     *
     * This became load-bearing the moment the lobby grew a Leave button: the
     * caller in `main.ts` is suspended on `waitBegin` while the screen is up, and
     * walking out without rejecting it left that call suspended forever — a
     * closure holding a session, a world and a menu, for a room the player is no
     * longer in. `onclose` cannot do it, because `closed` is set here first
     * precisely so that leaving is not reported as the line dropping.
     */
    for (const w of this.beginWaiters) w.no(new Error(this.closed));
    this.beginWaiters.length = 0;
    this.onLobby = null;
    try {
      this.ws.close();
    } catch {
      /* already gone */
    }
    this.statusEl?.remove();
    this.statusEl = null;
  }

  /** How long since the board last arrived, in seconds. */
  quietFor(): number {
    return this.lastHeard ? (Date.now() - this.lastHeard) / 1000 : 0;
  }

  private send(m: unknown): void {
    if (this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(m));
  }

  /**
   * The room, on screen: who is in it and what it is doing.
   *
   * The net layer owns exactly this one panel. Seats are listed by name with the
   * ones who have gone struck through rather than removed, because "Ada left" is
   * information and an empty space is not — and a seat can come back, at which
   * point the line simply lights again.
   */
  private paint(error?: string): void {
    if (!this.statusEl) {
      this.statusEl = document.createElement('div');
      this.statusEl.id = 'net-status';
      this.statusEl.style.cssText =
        'position:fixed;top:52px;right:12px;z-index:60;min-width:150px;' +
        'font:11px ui-monospace,monospace;padding:8px 11px 9px;' +
        'background:rgba(20,18,15,0.9);color:#a89e8c;border:1px solid #2a2724;' +
        'border-radius:3px;pointer-events:none;line-height:1.65';
      document.body.appendChild(this.statusEl);
    }
    if (error) {
      this.statusEl.textContent = `co-op: ${error}`;
      this.statusEl.style.color = '#9c3336';
      return;
    }
    if (this.closed) {
      this.statusEl.innerHTML = `<b style="color:#9c3336">The room closed</b><br>${esc(this.closed)}`;
      return;
    }
    const gone = this.players.filter((p) => !p.alive).length;
    const state = this.quiet
      ? '<span style="color:#9c3336">the realm has gone quiet…</span>'
      : this.paused
        ? `<span style="color:#c8a44c">paused by ${esc(this.pausedBy || 'somebody')}</span>`
        : this.begun
          ? ''
          : '<span style="color:#c8a44c">waiting for allies…</span>';
    const rows = this.players
      .map((p) => {
        const me = p.id === this.playerId ? ' <span style="color:#6d675e">(you)</span>' : '';
        return p.alive
          ? `<div><span style="color:#c8a44c">●</span> <span style="color:#d9cfbc">${esc(p.name)}</span>${me}</div>`
          : `<div style="color:#6d675e"><span>○</span> <s>${esc(p.name)}</s> — left</div>`;
      })
      .join('');
    this.statusEl.innerHTML =
      `<div style="color:#8a6c26;letter-spacing:.14em;text-transform:uppercase;font-size:9.5px">` +
      `${esc(this.room)}${gone ? ` · ${gone} left` : ''}</div>${rows}` +
      (state ? `<div style="margin-top:3px">${state}</div>` : '');
    this.statusEl.style.color = '#a89e8c';
  }
}
